/**
 * InstanceDetailView — full-width detail page for a selected plugin instance,
 * shown in the main content area when an instance is selected from the sidebar.
 */

import type { PluginDefinition, PluginOutput } from "@magic-md/core";
import { REF_RE } from "@magic-md/core/patterns";
import type {
    ActionDeclaration,
    ConfigPanelContext,
} from "@magic-md/plugin-sdk";
import { JsonTree } from "@magic-md/plugin-sdk";
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle2,
    Clock,
    Code2,
    Layers,
    Play,
    Puzzle,
    Settings,
    Square,
    XCircle,
    Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useT } from "@magic-md/i18n/react";
import { useDefinitionUseBinding } from "../hooks/useDefinitionUseBinding";
import { useDocumentAssetReferences } from "../hooks/useDocumentAssetReferences";
import { useDraftConfig } from "../hooks/useDraftConfig";
import { usePluginUI } from "../hooks/usePluginUI";
import { attachDefinitionOutputs, buildConfigPanelUnits } from "../lib/config-panel-units";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { collectImportDocuments } from "../lib/import-documents";
import { pickerInitialPathFromDocument } from "../lib/path-utils";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { runDefinitionWithFallback } from "../lib/run-definition";
import {
    collectScopeVariables,
    collectTemplateInputScopeVariables,
    getMergedDefinitions,
} from "../lib/scope-variables";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import {
    type ExecutionStatus,
    usePluginExecutionStore,
} from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { showFilePickerDialog, showFolderPickerDialog } from "./dialogs";
import {
    ConfigEditorModeButton,
    DirtyStateBanner,
    RawConfigEditor,
} from "./shared";
import { UseBindEditor } from "./UseBindEditor";
import { UseWithEditor } from "./UseWithEditor";

/* ── Sub-components ── */

function Section({
    title,
    icon,
    children,
    actions,
}: {
    title: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                {icon && <span className="text-muted-foreground/60">{icon}</span>}
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {title}
                </h3>
                {actions && (
                    <>
                        <div className="flex-1" />
                        {actions}
                    </>
                )}
            </div>
            {children}
        </div>
    );
}

function StatusBadge({
    status,
    duration,
}: {
    status: ExecutionStatus;
    duration?: number | null;
}) {
    const t = useT("editor");
    switch (status) {
        case "running":
            return (
                <span
                    className="theme-accent-chip inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={getThemeAccentStyle("running")}
                >
                    <span className="relative flex h-[6px] w-[6px]">
                        <span
                            className="theme-accent-fill absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                            style={getThemeAccentStyle("running")}
                        />
                        <span
                            className="theme-accent-fill relative inline-flex h-[6px] w-[6px] rounded-full"
                            style={getThemeAccentStyle("running")}
                        />
                    </span>
                    {t("instance.status.running")}
                </span>
            );
        case "success":
            return (
                <span
                    className="theme-accent-chip inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={getThemeAccentStyle("success")}
                >
                    <CheckCircle2 size={10} />
                    {t("instance.status.success")}
                    {duration != null && (
                        <span className="text-[9px] opacity-70">{duration}ms</span>
                    )}
                </span>
            );
        case "error":
            return (
                <span
                    className="theme-accent-chip inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={getThemeAccentStyle("error")}
                >
                    <XCircle size={10} />
                    {t("instance.status.error")}
                    {duration != null && (
                        <span className="text-[9px] opacity-70">{duration}ms</span>
                    )}
                </span>
            );
        default:
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
                    {t("instance.status.idle")}
                </span>
            );
    }
}

/* ── Main Component ── */

export function InstanceDetailView({ instanceId }: { instanceId: string }) {
    const t = useT("editor");
    const setSelectedInstanceId = useEditorStore((s) => s.setSelectedInstanceId);
    const setSelectedPluginName = useEditorStore((s) => s.setSelectedPluginName);
    const setSidebarView = useEditorStore((s) => s.setSidebarView);
    const parsedDocument = useEditorStore((s) => s.parsedDocument);
    const activeFilePath = useEditorStore(
        (s) => s.tabs.find((tab) => tab.id === s.activeTabId)?.filePath,
    );
    const documentPath = activeFilePath ?? null;
    const documentBundlePath = documentPath ? getDocumentBundleDir(documentPath) : null;
    const pickerInitialPath = pickerInitialPathFromDocument(activeFilePath);

    const definitions = useMemo(
        () => getMergedDefinitions(parsedDocument),
        [parsedDocument],
    );

    const contentDefinitions = useMemo(
        () => parsedDocument?.contentDefinitions ?? new Map(),
        [parsedDocument],
    );

    const definition = useMemo(
        () => definitions.get(instanceId) ?? null,
        [definitions, instanceId],
    );

    const plugin = usePluginUI(definition?.pluginName);

    const executions = usePluginExecutionStore((s) => s.executions);
    const forExecutions = usePluginExecutionStore((s) => s.forExecutions);
    const contentExecutions = usePluginExecutionStore((s) => s.contentExecutions);
    const importExecutions = usePluginExecutionStore((s) => s.importExecutions);
    const useExecutions = usePluginExecutionStore((s) => s.useExecutions);
    const definitionOutputs = useMemo(() => {
        const outputs = new Map<string, PluginOutput>();
        for (const [id, definition] of definitions) {
            if (definition.output) outputs.set(id, definition.output);
        }
        for (const [id, entry] of executions) {
            if (entry.output) outputs.set(id, entry.output);
        }
        return outputs;
    }, [definitions, executions]);
    const definitionsWithOutputs = useMemo(
        () => attachDefinitionOutputs(definitions, definitionOutputs),
        [definitions, definitionOutputs],
    );
    const availableUnits = useMemo(
        () => buildConfigPanelUnits({
            definitions: definitionsWithOutputs,
            definitionOutputs,
            forDefinitions: parsedDocument?.forDefinitions,
            contentDefinitions,
            imports: parsedDocument?.imports,
            uses: collectParsedDocumentUseBindings(parsedDocument),
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
        }),
        [
            definitionsWithOutputs,
            definitionOutputs,
            parsedDocument,
            contentDefinitions,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
        ],
    );
    const execution = useMemo(
        () => executions.get(instanceId) ?? null,
        [executions, instanceId],
    );
    const runPrimary = usePluginExecutionStore((s) => s.runPrimary);
    const runAction = usePluginExecutionStore((s) => s.runAction);
    const runForAction = usePluginExecutionStore((s) => s.runForAction);
    const runUseAction = usePluginExecutionStore((s) => s.runUseAction);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const clearExecution = usePluginExecutionStore((s) => s.clearExecution);

    const hasParsedOutput = !execution && !!definition?.output;
    const status: ExecutionStatus =
        execution?.status ?? (hasParsedOutput ? "success" : "idle");
    const isRunning = status === "running";

    const [useRawEditor, setUseRawEditor] = useState(false);
    const lastInstanceIdRef = useState(() => ({ current: instanceId }))[0];

    const importDocuments = useMemo(
        () => collectImportDocuments(parsedDocument?.imports, importExecutions),
        [parsedDocument?.imports, importExecutions],
    );
    const availableAssets = useDocumentAssetReferences(activeFilePath, importDocuments);

    // Reset raw editor state when switching instances
    useEffect(() => {
        if (lastInstanceIdRef.current === instanceId) return;
        lastInstanceIdRef.current = instanceId;
        setUseRawEditor(false);
    }, [instanceId, lastInstanceIdRef]);

    /* Draft config — edits stay local until Run or Apply */
    // For use-derived definitions, pass the source definition's config to compute patch
    // Find the ::use binding for this instance (if use-derived)
    const {
        currentUseBinding,
        useSourceConfig,
        importSymbols,
        templateParams,
        templateInputSchema,
        missingRequiredTemplateInputs,
    } =
        useDefinitionUseBinding(
            instanceId,
            parsedDocument ?? undefined,
            definitions,
            contentDefinitions,
            importExecutions,
        );
    const isTemplateUnit = currentUseBinding
        ? currentUseBinding.template === true || missingRequiredTemplateInputs.length > 0
        : !!definition?.template;

    const { draftConfig, isDirty, updateDraft, applyDraft, discardDraft } =
        useDraftConfig(
            instanceId,
            definition?.config as Record<string, unknown> | undefined,
            {
                unitKind: currentUseBinding ? "use" : "define",
                ...(useSourceConfig ? {
                    sourceConfig: useSourceConfig,
                    bind: currentUseBinding?.bind,
                    with: currentUseBinding?.with,
                } : {}),
                sourceRange: currentUseBinding?.range,
            },
        );

    /* ConfigPanelContext */
    const configContext = useMemo<ConfigPanelContext>(
        () => ({
            currentDefinitionId: definition?.id ?? "",
            documentPath,
            documentBundlePath,
            selectFile: async (options) => {
                const result = await showFilePickerDialog({
                    accept: options?.accept,
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            selectDirectory: async () => {
                const result = await showFolderPickerDialog({
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            getAvailableDefinitions: () => definitionsWithOutputs,
            getAvailableUnits: () => availableUnits,
            getAvailableContentDefinitions: () => contentDefinitions,
            getAvailableImportDocuments: () => importDocuments,
            getAvailableAssets: () => availableAssets,
            previewAction: async (actionName: string) => {
                if (!definition) return { success: false, message: "No definition" };
                if (isTemplateUnit) {
                    return { success: false, message: t("instance.templateNotExecutable") };
                }
                return runAction(definition, actionName, definitions);
            },
            callSidecar: async (sidecarId, method, params) =>
                getPlatformService().engine.sidecarCall(sidecarId, method, params),
            scopeVariables: definition
                ? [
                    ...collectTemplateInputScopeVariables(
                        definition.inputSchema,
                        definition.params,
                        draftConfig,
                    ),
                    ...collectScopeVariables(
                        definition.id,
                        parsedDocument?.blocks ?? [],
                        parsedDocument?.forDefinitions,
                        definitions,
                        { uses: collectParsedDocumentUseBindings(parsedDocument), contentDefinitions },
                    ),
                ]
                : undefined,
        }),
        [
            definitions,
            definitionsWithOutputs,
            availableUnits,
            contentDefinitions,
            importDocuments,
            definition,
            runAction,
            isTemplateUnit,
            t,
            parsedDocument,
            draftConfig,
            pickerInitialPath,
            availableAssets,
            documentPath,
            documentBundlePath,
        ],
    );

    if (!definition) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3">
                <Puzzle size={32} className="text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                    {t("instance.notFound.title", { id: instanceId })}
                </p>
                <p className="text-[11px] text-muted-foreground/50">
                    {t("instance.notFound.detail")}
                </p>
                <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setSelectedInstanceId(null)}
                >
                    {t("common.back")}
                </button>
            </div>
        );
    }

    const hasPluginConfigPanel = !!plugin?.ConfigPanel;
    const showVisualConfig = hasPluginConfigPanel && !useRawEditor;
    const canRunUnit = !!plugin && !isTemplateUnit;
    const outputData = execution?.output ?? definition.output ?? undefined;

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setSelectedInstanceId(null)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />
                <span className="text-xs text-muted-foreground">{t("instance.detailTitle")}</span>

                <div className="flex-1" />

                {/* Run / Stop */}
                {canRunUnit &&
                    (isRunning ? (
                        <button
                            type="button"
                            className="theme-accent-button flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors"
                            style={getThemeAccentStyle("error")}
                            onClick={() => cancelExecution(definition.id)}
                        >
                            <Square size={11} className="fill-current" />
                            {t("common.stop")}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="theme-accent-button flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors"
                            style={getThemeAccentStyle("success")}
                            onClick={() => {
                                if (currentUseBinding) {
                                    if (isDirty) applyDraft();
                                    void runUseAction(currentUseBinding.id);
                                    return;
                                }
                                void runDefinitionWithFallback({
                                    definition,
                                    isDirty,
                                    applyDraft,
                                    runPrimary,
                                    runForAction,
                                });
                            }}
                        >
                            <Play size={11} className="fill-current" />
                            {t("common.run")}
                        </button>
                    ))}
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
                    {/* Header */}
                    <div className="space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <Layers size={20} className="text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-lg font-bold text-foreground truncate">
                                    {definition.id}
                                </h1>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <button
                                        type="button"
                                        className="text-xs text-primary hover:underline"
                                        onClick={() => {
                                            setSelectedInstanceId(null);
                                            setSidebarView("engine");
                                            setSelectedPluginName(definition.pluginName);
                                        }}
                                    >
                                        {definition.pluginName}
                                    </button>
                                    <StatusBadge status={status} duration={execution?.duration} />
                                </div>
                            </div>
                        </div>

                        {/* Plugin not found warning */}
                        {!plugin && (
                            <div
                                className="theme-accent-banner flex items-center gap-2 rounded-lg px-3 py-2 text-xs"
                                style={getThemeAccentStyle("warning")}
                            >
                                <AlertTriangle size={14} className="shrink-0" />
                                <span>
                                    {t("instance.pluginMissing", {
                                        name: definition.pluginName,
                                        availableLabel: t("instance.availablePlugins"),
                                        available: pluginUIRegistry.names().join(", ") || t("common.none"),
                                    })}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Execution info */}
                    {execution && (
                        <Section title={t("instance.section.executionInfo")} icon={<Clock size={14} />}>
                            <div className="rounded-lg border border-border/40 divide-y divide-border/30">
                                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-4 py-3 text-xs">
                                    <span className="text-muted-foreground">{t("common.status")}</span>
                                    <StatusBadge
                                        status={execution.status}
                                        duration={execution.duration}
                                    />
                                    {execution.status === "running" && execution.progress && (
                                        <>
                                            <span className="text-muted-foreground">{t("common.progress")}</span>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-foreground/80 text-xs truncate">
                                                    {execution.progress.message}
                                                </span>
                                                {execution.progress.percent != null && (
                                                    <div
                                                        className="theme-accent-progress-track h-1.5 w-full overflow-hidden rounded-full"
                                                        style={getThemeAccentStyle("running")}
                                                    >
                                                        <div
                                                            className="theme-accent-progress-fill h-full rounded-full transition-[width] duration-300"
                                                            style={{
                                                                ...getThemeAccentStyle("running"),
                                                                width: `${Math.min(100, Math.max(0, execution.progress.percent))}%`,
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {execution.actionName && (
                                        <>
                                            <span className="text-muted-foreground">{t("common.action")}</span>
                                            <code className="font-mono text-foreground/80">
                                                {execution.actionName}
                                            </code>
                                        </>
                                    )}
                                    {execution.startedAt && (
                                        <>
                                            <span className="text-muted-foreground">{t("common.startTime")}</span>
                                            <span className="text-foreground/70">
                                                {new Date(execution.startedAt).toLocaleTimeString()}
                                            </span>
                                        </>
                                    )}
                                    {execution.duration != null && (
                                        <>
                                            <span className="text-muted-foreground">{t("common.duration")}</span>
                                            <span className="text-foreground/70">
                                                {execution.duration}ms
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Section>
                    )}

                    {/* Error */}
                    {execution?.result && !execution.result.success && (
                        <div
                            className="theme-accent-banner flex items-start gap-2 rounded-lg p-3 text-xs"
                            style={getThemeAccentStyle("error")}
                        >
                            <XCircle size={14} className="shrink-0 mt-0.5" />
                            <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all font-mono">
                                {execution.result.message}
                            </pre>
                            <button
                                type="button"
                                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-icon-button"
                                style={getThemeAccentStyle("error")}
                                onClick={() => clearExecution(definition.id)}
                            >
                                <XCircle size={10} className="inline-block mr-0.5" />
                                {t("common.clear")}
                            </button>
                        </div>
                    )}

                    {/* Output */}
                    {outputData && (
                        <Section title={t("common.output")} icon={<Code2 size={14} />}>
                            <div
                                className={cn(
                                    "rounded-lg border overflow-hidden",
                                    status === "error" ? "theme-accent-border" : "border-border/40",
                                )}
                                style={
                                    status === "error"
                                        ? getThemeAccentStyle("error")
                                        : undefined
                                }
                            >
                                <div className="flex items-center px-3 py-1.5 bg-muted/30 border-b border-border/30">
                                    <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                                        JSON
                                    </span>
                                </div>
                                <div className="px-3 py-2 overflow-auto max-h-[400px]">
                                    <JsonTree value={outputData} defaultExpandDepth={2} />
                                </div>
                            </div>
                        </Section>
                    )}

                    {/* Bind / With editors for ::use blocks */}
                    {currentUseBinding && (useSourceConfig || templateParams || templateInputSchema) && (
                        <Section title={t("instance.section.useConfig")} icon={<Settings size={14} />}>
                            <div className="space-y-3">
                                {useSourceConfig && (
                                    <UseBindEditor
                                        useBinding={currentUseBinding}
                                        definitions={definitions}
                                        sourceConfig={useSourceConfig}
                                        importSymbols={importSymbols}
                                    />
                                )}
                                {(templateParams || templateInputSchema) && (
                                    <UseWithEditor
                                        useBinding={currentUseBinding}
                                        templateParams={templateParams ?? []}
                                        inputSchema={templateInputSchema}
                                        definitions={definitions}
                                    />
                                )}
                            </div>
                        </Section>
                    )}

                    {/* Config section */}
                    <Section
                        title={t("common.config")}
                        icon={<Settings size={14} />}
                        actions={
                            hasPluginConfigPanel ? (
                                <ConfigEditorModeButton
                                    useRawEditor={useRawEditor}
                                    onChange={setUseRawEditor}
                                />
                            ) : undefined
                        }
                    >
                        {isDirty && (
                            <div className="mb-3">
                                <DirtyStateBanner
                                    onSave={applyDraft}
                                    onDiscard={discardDraft}
                                />
                            </div>
                        )}

                        {showVisualConfig && plugin?.ConfigPanel ? (
                            <div className="rounded-lg border border-border bg-background p-4">
                                <div>
                                    <plugin.ConfigPanel
                                        value={draftConfig}
                                        onChange={updateDraft}
                                        context={configContext}
                                    />
                                </div>
                            </div>
                        ) : (
                            <RawConfigEditor
                                config={draftConfig}
                                onChange={updateDraft}
                                textareaClassName="h-60"
                            />
                        )}
                    </Section>

                    {/* Actions */}
                    {canRunUnit && plugin && plugin.manifest.actions.length > 0 && (
                        <Section title={t("common.actions")} icon={<Zap size={14} />}>
                            <div className="flex flex-wrap gap-2">
                                {plugin.manifest.actions.map((action: ActionDeclaration) => (
                                    <button
                                        type="button"
                                        key={action.name}
                                        disabled={isRunning}
                                        className={cn(
                                            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                                            action.primary
                                                ? "bg-primary/10 border-primary/20 text-primary hover:bg-primary/20"
                                                : action.dangerous
                                                    ? "border-destructive/30 text-destructive hover:bg-destructive/10"
                                                    : "border-border text-foreground hover:bg-muted",
                                            isRunning && "opacity-50 cursor-not-allowed",
                                        )}
                                        onClick={() =>
                                            runAction(definition, action.name, definitions)
                                        }
                                    >
                                        {action.icon && <span>{action.icon}</span>}
                                        {action.label || action.name}
                                        {action.primary && (
                                            <span className="text-[9px] opacity-50">{t("instance.primaryAction")}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Dependencies (refs) */}
                    <RefsSection
                        config={definition.config}
                        definitions={definitions}
                        onNavigate={(id) => setSelectedInstanceId(id)}
                    />
                </div>
            </div>
        </div>
    );
}

/* ── Refs section — shows $ref dependencies ── */

function RefsSection({
    config,
    definitions,
    onNavigate,
}: {
    config: Record<string, unknown>;
    definitions: Map<string, PluginDefinition>;
    onNavigate: (id: string) => void;
}) {
    const t = useT("editor");
    const getOutput = usePluginExecutionStore((s) => s.getOutput);

    const refs = useMemo(() => {
        const configStr = JSON.stringify(config);
        const matches = configStr.match(new RegExp(REF_RE.source, "g"));
        if (!matches) return [];
        const seen = new Set<string>();
        const result: {
            refId: string;
            path: string;
            status: "resolved" | "not-found" | "not-executed" | "path-invalid";
            value?: unknown;
        }[] = [];
        for (const m of matches) {
            const full = m.replace("$ref{", "").replace("}", "");
            const refId = full.split(".")[0];
            if (!seen.has(full)) {
                seen.add(full);

                // Detect accessor: body vs output
                const dotIdx = full.indexOf(".");
                const rest = dotIdx >= 0 ? full.slice(dotIdx + 1) : "";

                // Body accessor — resolve from contentDefinitions
                if (rest === "body" || rest.startsWith("body.")) {
                    const parsedDoc = useEditorStore.getState().parsedDocument;
                    const contentDef = parsedDoc?.contentDefinitions?.get(refId);
                    if (!contentDef) {
                        result.push({ refId, path: full, status: "not-found" });
                    } else {
                        result.push({
                            refId,
                            path: full,
                            status: "resolved",
                            value: contentDef.body,
                        });
                    }
                    continue;
                }

                // Output accessor — resolve from definitions
                if (!definitions.has(refId)) {
                    result.push({ refId, path: full, status: "not-found" });
                } else {
                    const def = definitions.get(refId);
                    if (!def) {
                        result.push({ refId, path: full, status: "not-found" });
                        continue;
                    }
                    const execOutput = getOutput(refId);
                    const output = execOutput ?? def.output ?? undefined;
                    if (!output) {
                        result.push({ refId, path: full, status: "not-executed" });
                    } else {
                        // Navigate path
                        const outputPath = rest.startsWith("output.")
                            ? rest.slice("output.".length)
                            : rest === "output"
                                ? ""
                                : rest;

                        let value: unknown = output;
                        if (outputPath) {
                            for (const seg of outputPath.split(".")) {
                                if (value == null || typeof value !== "object") {
                                    value = undefined;
                                    break;
                                }
                                value = (value as Record<string, unknown>)[seg];
                            }
                        }
                        if (value === undefined && outputPath) {
                            result.push({ refId, path: full, status: "path-invalid" });
                        } else {
                            result.push({ refId, path: full, status: "resolved", value });
                        }
                    }
                }
            }
        }
        return result;
    }, [config, definitions, getOutput]);

    if (refs.length === 0) return null;

    return (
        <Section title={t("common.dependencies")} icon={<Layers size={14} />}>
            <div className="space-y-1">
                {refs.map((ref) => (
                    <div
                        key={ref.path}
                        className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2"
                    >
                        <code className="text-[11px] font-mono text-foreground/70 flex-1 truncate">
                            $ref&#123;{ref.path}&#125;
                        </code>
                        {ref.status === "resolved" ? (
                            <span
                                className="theme-accent-text max-w-[120px] shrink-0 truncate text-[10px] font-mono"
                                style={getThemeAccentStyle("success")}
                                title={String(ref.value)}
                            >
                                = {formatRefPreview(ref.value)}
                            </span>
                        ) : ref.status === "not-executed" ? (
                            <span
                                className="theme-accent-text flex shrink-0 items-center gap-0.5 text-[10px]"
                                style={getThemeAccentStyle("info")}
                            >
                                <Clock size={9} />
                                {t("instance.ref.notExecuted")}
                            </span>
                        ) : ref.status === "path-invalid" ? (
                            <span
                                className="theme-accent-text flex shrink-0 items-center gap-0.5 text-[10px]"
                                style={getThemeAccentStyle("warning")}
                            >
                                <AlertTriangle size={9} />
                                {t("instance.ref.pathInvalid")}
                            </span>
                        ) : (
                            <span
                                className="theme-accent-text flex shrink-0 items-center gap-0.5 text-[10px]"
                                style={getThemeAccentStyle("error")}
                            >
                                <XCircle size={9} />
                                {t("instance.ref.notFound")}
                            </span>
                        )}
                        {definitions.has(ref.refId) && (
                            <button
                                type="button"
                                className="text-[10px] text-primary hover:underline shrink-0"
                                onClick={() => onNavigate(ref.refId)}
                            >
                                →
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </Section>
    );
}

/** Format a ref value for inline preview */
function formatRefPreview(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string")
        return value.length > 30 ? `"${value.slice(0, 27)}…"` : `"${value}"`;
    if (typeof value === "number" || typeof value === "boolean")
        return String(value);
    if (Array.isArray(value)) return `[${value.length} items]`;
    if (typeof value === "object") return `{${Object.keys(value).length} keys}`;
    return String(value);
}
